import { Box, Chip, Grid } from "@material-ui/core";
import { red } from "@material-ui/core/colors";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import {
    Championship,
    Collections,
    Incident,
    IncidentDefaultValues,
    Infraction,
    isIncidentDone,
    PenaltyCommandByType,
    PenaltyDescriptionByType,
    PenaltyPoints,
    PenaltyType,
    RaceControl,
    StatusIncident,
    StatusIncidentColor,
    StatusIncidentDescription,
    StatusIncidentOrder,
    StatusRaceControl,
    useChampionshipDrivers,
    useCollection,
} from "api";
import copy from "copy-to-clipboard";
import cloneDeep from "lodash/cloneDeep";
import MaterialTable from "material-table";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useFirestore, useFirestoreConnect } from "react-redux-firebase";
import { RootState } from "redux/model";
import {
    BoxCard,
    ConfirmationDialog,
    ConfirmationDialogState,
    Message,
} from "ui/componentes";
import { CarsNumbersToChip } from "ui/views/ChampionshipForm/RoundEdit/CarsNumbersToChip";
import {
    addBroadcastMessageIfNotExists,
    IncidentForm,
} from "ui/views/ChampionshipForm/RoundEdit/IncidentForm";
import { LocalizationPTBr } from "util/MaterialTableUtil";

interface IncidentListProps {
    raceControl: RaceControl;
    championship: Championship;
}

const isIncidentDoneOrAfterRace = (incident: Incident) => {
    return (
        isIncidentDone(incident) ||
        incident.status === StatusIncident.AFTER_RACE
    );
};

const isCopyCommandHidden = (
    incident: Incident,
    raceControl: RaceControl,
    infractions
) => {
    return (
        isIncidentDoneOrAfterRace(incident) ||
        incident.status === StatusIncident.NOTED ||
        raceControl.status === StatusRaceControl.FINISHED ||
        !incident.infractionId ||
        (infractions[incident.infractionId] &&
            !PenaltyCommandByType[
                infractions[incident.infractionId]["penaltyType"]
            ])
    );
};

const isApplyPenaltyHidden = (incident: Incident, raceControl: RaceControl) => {
    return (
        isIncidentDoneOrAfterRace(incident) ||
        incident.status === StatusIncident.NOTED ||
        raceControl.status === StatusRaceControl.FINISHED ||
        !incident.infractionId
    );
};

const clearConfirmation = (setConfirmation) => () =>
    setConfirmation({ message: "", showDialog: false });

const confirmIncidentUpdate = (
    firestore,
    incident,
    enqueueSnackbar,
    setConfirmation,
    status: StatusIncident,
    addBroadcastMessageIfNotExists
) => () => {
    return firestore
        .collection(Collections.RACES_CONTROL)
        .doc(incident?.raceControlId)
        .collection(Collections.INCIDENTS)
        .doc(incident?.id)
        .update({ status })
        .then(() => {
            enqueueSnackbar("Incidente atualizado!", {
                variant: "success",
                anchorOrigin: {
                    horizontal: "right",
                    vertical: "bottom",
                },
            });
            clearConfirmation(setConfirmation)();

            if (
                addBroadcastMessageIfNotExists &&
                incident.involvedCars.length > 1
            ) {
                addBroadcastMessageIfNotExists().catch(console.error);
            }
            return firestore
                .collection(Collections.RACES_CONTROL)
                .doc(incident?.raceControlId)
                .collection(Collections.INCIDENTS)
                .doc(incident?.id)
                .get()
                .then((doc) =>
                    doc.exists ? { ...doc.data(), id: doc.id } : undefined
                );
        })
        .catch((error) => {
            console.error(error);
            enqueueSnackbar("Não foi possível atualizar incidente!", {
                variant: "error",
                anchorOrigin: {
                    horizontal: "right",
                    vertical: "bottom",
                },
            });
            clearConfirmation(setConfirmation)();
        });
};

const IncidentList: React.FC<IncidentListProps> = ({
    raceControl,
    championship,
}) => {
    const [confirmation, setConfirmation] = useState<ConfirmationDialogState>({
        showDialog: false,
        message: "",
    });
    const [incident, setIncident] = useState<Incident>({
        ...IncidentDefaultValues,
        raceControlId: raceControl.id || "-1",
    });

    const penaltyPoints = useCollection<PenaltyPoints[]>(
        {
            collection: Collections.PENALTY_POINTS,
            where: ["championshipId", "==", championship?.id || "-1"],
        },
        true
    );

    useEffect(() => {
        setIncident({
            ...IncidentDefaultValues,
            raceControlId: raceControl.id || "-1",
        });
    }, [raceControl]);

    const { enqueueSnackbar } = useSnackbar();
    const firestore = useFirestore();

    useFirestoreConnect({
        collection: Collections.RACES_CONTROL,
        doc: raceControl.id || "-1",
        subcollections: [{ collection: Collections.INCIDENTS }],
        storeAs: `incidents${raceControl.id || "List"}`,
    });

    const incidents: Incident[] = cloneDeep(
        useSelector((state: RootState) => {
            return state.firestore.ordered[
                `incidents${raceControl.id || "List"}`
            ];
        }) || []
    );

    incidents.sort((i1, i2) => {
        if (StatusIncidentOrder[i1.status] < StatusIncidentOrder[i2.status])
            return -1;
        if (StatusIncidentOrder[i1.status] > StatusIncidentOrder[i2.status])
            return 1;
        if (i1.lap < i2.lap) return -1;
        if (i1.lap > i2.lap) return 1;
        return 0;
    });

    const drivers = useChampionshipDrivers(championship?.id);

    const driversByNumber = useMemo(() => {
        if (championship) {
            const driversByNumber = {};
            if (drivers && drivers.length > 0) {
                drivers.forEach((driver) => {
                    driversByNumber[
                        driver.usedNumber
                    ] = `#${driver.usedNumber} - ${driver.displayName}`;
                });
            }
            return driversByNumber;
        }
        return {};
    }, [championship, drivers]);

    const driversByIRacingId = useMemo(() => {
        if (championship) {
            const driversByIRacingId = {};
            if (drivers && drivers.length > 0) {
                drivers.forEach((driver) => {
                    driversByIRacingId[
                        driver.iracingId || ""
                    ] = `#${driver.usedNumber} - ${driver.displayName}`;
                });
            }
            return driversByIRacingId;
        }
        return {};
    }, [championship, drivers]);

    const suspendedDriversMessage = useMemo(() => {
        if (!penaltyPoints.length || !drivers.length) return "";

        const suspendedDriversByRound =
            penaltyPoints[0].suspendedDriversByRound || {};
        const suspendedDrivers =
            suspendedDriversByRound[raceControl.roundId || ""];

        if (!suspendedDrivers) return "";

        return Object.keys(suspendedDrivers)
            .filter(
                (iRacingId) =>
                    suspendedDrivers[iRacingId] && driversByIRacingId[iRacingId]
            )
            .map((iRacingId) => driversByIRacingId[iRacingId])
            .join(" | ");
    }, [
        drivers.length,
        driversByIRacingId,
        penaltyPoints,
        raceControl.roundId,
    ]);

    const driversToMessages = useMemo(() => {
        if (championship) {
            const driversByNumber = {};
            if (drivers && drivers.length > 0) {
                drivers.forEach((driver) => {
                    driversByNumber[driver.usedNumber] = driver;
                });
            }
            return driversByNumber;
        }
        return {};
    }, [championship, drivers]);

    const infractions =
        useCollection<object>({
            collection: Collections.INFRACTIONS,
        }) || {};

    return (
        <Grid container spacing={2}>
            {!!suspendedDriversMessage && (
                <Box width="100%" padding={1}>
                    <Message
                        variant="error"
                        show
                        message={`Piloto(s) suspenso(s): ${suspendedDriversMessage}`}
                    />
                </Box>
            )}
            <ConfirmationDialog
                title={confirmation.title}
                showDialog={confirmation.showDialog}
                message={confirmation.message}
                onCancel={clearConfirmation(setConfirmation)}
                onOk={confirmation.onOk}
            />
            <Grid
                item
                xs={12}
                hidden={raceControl.status === StatusRaceControl.FINISHED}
            >
                <IncidentForm
                    championship={championship}
                    incident={incident}
                    onCancel={() => {
                        setIncident({
                            ...IncidentDefaultValues,
                            raceControlId: raceControl.id || "-1",
                        });
                    }}
                    roundId={raceControl.roundId || ""}
                    drivers={drivers}
                />
            </Grid>
            <Grid item xs={12}>
                <MaterialTable
                    title="Incidentes"
                    components={{ Container: BoxCard }}
                    columns={[
                        {
                            field: "lap",
                            title: "Volta",
                            align: "center",
                            editable: "never",
                        },
                        {
                            field: "time",
                            title: "Min. / seg. do incidente",
                            align: "center",
                            editable: "never",
                        },
                        {
                            field: "notifiedBy",
                            title: "Notificado por",
                            editable: "never",
                            render: (incident) => {
                                if (incident.notifiedBy) {
                                    return (
                                        <CarsNumbersToChip
                                            carNumbers={incident.notifiedBy}
                                            driversByNumber={driversByNumber}
                                        />
                                    );
                                }
                                return "";
                            },
                        },
                        {
                            field: "involvedCars",
                            title: "Carros envolvidos",
                            render: (incident) => {
                                if (incident.involvedCars) {
                                    return (
                                        <CarsNumbersToChip
                                            carNumbers={incident.involvedCars}
                                            driversByNumber={driversByNumber}
                                            color="primary"
                                        />
                                    );
                                }
                                return "";
                            },
                            editable: "never",
                        },
                        {
                            field: "guiltyCar",
                            title: "Culpado",
                            render: (incident) => {
                                if (incident.guiltyCar) {
                                    return (
                                        <CarsNumbersToChip
                                            carNumbers={
                                                incident.guiltyCar === "-1"
                                                    ? []
                                                    : [incident.guiltyCar]
                                            }
                                            driversByNumber={driversByNumber}
                                            style={{
                                                backgroundColor: red[500],
                                                color: "white",
                                            }}
                                        />
                                    );
                                }
                                return "";
                            },
                            editable: "never",
                        },
                        {
                            field: "infractionId",
                            title: "Infração / Penalidade",
                            render: (incident) => {
                                if (incident.infractionId) {
                                    const infraction: Infraction =
                                        infractions[incident.infractionId];
                                    if (!infraction) {
                                        return "";
                                    }
                                    return `${infraction.key} / ${
                                        PenaltyDescriptionByType[
                                            infraction.penaltyType || ""
                                        ]
                                    }`;
                                }
                                return "";
                            },
                            editable: "never",
                        },
                        {
                            field: "status",
                            title: "Status",
                            align: "center",
                            editable: "never",
                            render: (incident) => (
                                <Chip
                                    label={
                                        StatusIncidentDescription[
                                            incident.status
                                        ]
                                    }
                                    style={StatusIncidentColor[incident.status]}
                                    size="small"
                                />
                            ),
                        },
                    ]}
                    data={incidents}
                    actions={[
                        (incident) => ({
                            icon: "assignment",
                            tooltip: "Copiar comando para aplicar punição",
                            disabled: isCopyCommandHidden(
                                incident,
                                raceControl,
                                infractions
                            ),
                            onClick: (event, data) => {
                                const incident = data as Incident;
                                const infraction: Infraction =
                                    infractions[incident.infractionId || ""];

                                if (infraction && infraction.penaltyType) {
                                    const command =
                                        PenaltyCommandByType[
                                            infraction.penaltyType
                                        ];

                                    let commandText = "";

                                    const involvedCars = incident.involvedCars
                                        .filter(
                                            (carNumber) =>
                                                carNumber !== incident.guiltyCar
                                        )
                                        .map((carNumber) => `#${carNumber}`)
                                        .join(", ");

                                    const message = `Por incidente na volta ${incident.lap} com o(s) carro(s) (${involvedCars})`;

                                    if (
                                        infraction.penaltyType ===
                                        PenaltyType.STOP_AND_GO
                                    ) {
                                        commandText = command(
                                            incident.guiltyCar,
                                            infraction.stopAndGoTime,
                                            message
                                        );
                                    } else {
                                        commandText = command(
                                            incident.guiltyCar,
                                            message
                                        );
                                    }
                                    copy(commandText);
                                    enqueueSnackbar(
                                        `Comando  (${commandText}) copiado!`,
                                        {
                                            variant: "info",
                                            anchorOrigin: {
                                                vertical: "bottom",
                                                horizontal: "right",
                                            },
                                        }
                                    );
                                }
                            },
                        }),
                        (incident) => ({
                            icon: "check",
                            tooltip: "Marcar como aplicado",
                            disabled: isApplyPenaltyHidden(
                                incident,
                                raceControl
                            ),
                            onClick: () => {
                                if (incident.infractionId) {
                                    if (
                                        infractions[incident.infractionId][
                                            "penaltyType"
                                        ] === PenaltyType.WARNING
                                    ) {
                                        confirmIncidentUpdate(
                                            firestore,
                                            incident,
                                            enqueueSnackbar,
                                            setConfirmation,
                                            StatusIncident.APPLIED,
                                            () =>
                                                addBroadcastMessageIfNotExists(
                                                    firestore,
                                                    raceControl.roundId || "",
                                                    {
                                                        ...incident,
                                                        status:
                                                            StatusIncident.APPLIED,
                                                    },
                                                    driversToMessages,
                                                    infractions[
                                                        incident.infractionId ||
                                                            ""
                                                    ]
                                                )
                                        )();
                                    } else {
                                        setConfirmation({
                                            title:
                                                "Punição já foi aplicada no simulador?",
                                            message: `Após marcar o incidente como aplicado não será mais possível copiar o comando para aplicar a punição!`,
                                            showDialog: true,
                                            onOk: confirmIncidentUpdate(
                                                firestore,
                                                incident,
                                                enqueueSnackbar,
                                                setConfirmation,
                                                StatusIncident.APPLIED,
                                                () =>
                                                    addBroadcastMessageIfNotExists(
                                                        firestore,
                                                        raceControl.roundId ||
                                                            "",
                                                        {
                                                            ...incident,
                                                            status:
                                                                StatusIncident.APPLIED,
                                                        },
                                                        driversToMessages,
                                                        infractions[
                                                            incident.infractionId ||
                                                                ""
                                                        ]
                                                    )
                                            ),
                                        });
                                    }
                                }
                            },
                        }),
                        (incident) => ({
                            icon: () => <IndeterminateCheckBoxIcon />,
                            tooltip: "Nenhuma ação necessária",
                            disabled:
                                isIncidentDoneOrAfterRace(incident) ||
                                incident.status === StatusIncident.NOTED ||
                                raceControl.status ===
                                    StatusRaceControl.FINISHED,
                            onClick: () => {
                                setConfirmation({
                                    message: `Tem certeza que nenhuma ação será necessária?`,
                                    showDialog: true,
                                    onOk: confirmIncidentUpdate(
                                        firestore,
                                        incident,
                                        enqueueSnackbar,
                                        setConfirmation,
                                        StatusIncident.NO_FURTHER_ACTION,
                                        () =>
                                            addBroadcastMessageIfNotExists(
                                                firestore,
                                                raceControl.roundId || "",
                                                {
                                                    ...incident,
                                                    status:
                                                        StatusIncident.NO_FURTHER_ACTION,
                                                },
                                                driversToMessages
                                            )
                                    ),
                                });
                            },
                        }),
                        (incident) => ({
                            icon: "block",
                            tooltip: "Deixar para pós corrida",
                            disabled:
                                isIncidentDoneOrAfterRace(incident) ||
                                incident.status === StatusIncident.NOTED ||
                                raceControl.status ===
                                    StatusRaceControl.FINISHED,
                            onClick: () => {
                                setConfirmation({
                                    message: `Deixar incidente para pós corrida?`,
                                    showDialog: true,
                                    onOk: confirmIncidentUpdate(
                                        firestore,
                                        incident,
                                        enqueueSnackbar,
                                        setConfirmation,
                                        StatusIncident.AFTER_RACE,
                                        () =>
                                            addBroadcastMessageIfNotExists(
                                                firestore,
                                                raceControl.roundId || "",
                                                {
                                                    ...incident,
                                                    status:
                                                        StatusIncident.AFTER_RACE,
                                                },
                                                driversToMessages
                                            )
                                    ),
                                });
                            },
                        }),
                        (incident) => ({
                            icon: () => <PlayArrowIcon />,
                            tooltip: "Iniciar análise",
                            disabled:
                                incident.status !== StatusIncident.NOTED ||
                                raceControl.status ===
                                    StatusRaceControl.FINISHED,
                            onClick: () => {
                                confirmIncidentUpdate(
                                    firestore,
                                    incident,
                                    enqueueSnackbar,
                                    setConfirmation,
                                    StatusIncident.INVESTIGATING,
                                    () =>
                                        addBroadcastMessageIfNotExists(
                                            firestore,
                                            raceControl.roundId || "",
                                            {
                                                ...incident,
                                                status:
                                                    StatusIncident.INVESTIGATING,
                                            },
                                            driversToMessages
                                        )
                                )().then((result) => {
                                    if (result) {
                                        setIncident(result);
                                    }
                                });
                            },
                        }),
                        (incident) => ({
                            icon: "edit",
                            tooltip: "Editar",
                            disabled:
                                isIncidentDoneOrAfterRace(incident) ||
                                raceControl.status ===
                                    StatusRaceControl.FINISHED,
                            onClick: () => {
                                setIncident(incident);
                            },
                        }),
                    ]}
                    editable={{
                        isDeletable: (incident) =>
                            (incident.status === StatusIncident.NOTED ||
                                incident.status ===
                                    StatusIncident.INVESTIGATING) &&
                            raceControl.status !== StatusRaceControl.FINISHED,
                        onRowDelete: (incident) => {
                            return firestore
                                .collection(Collections.RACES_CONTROL)
                                .doc(raceControl.id)
                                .collection(Collections.INCIDENTS)
                                .doc(incident["id"])
                                .delete()
                                .then(() => {
                                    enqueueSnackbar("Incident removido!", {
                                        variant: "success",
                                        anchorOrigin: {
                                            horizontal: "right",
                                            vertical: "bottom",
                                        },
                                    });
                                })
                                .catch((error) => {
                                    console.error(error);
                                    enqueueSnackbar(
                                        "Não foi possível remover o incident!",
                                        {
                                            variant: "error",
                                            anchorOrigin: {
                                                horizontal: "right",
                                                vertical: "bottom",
                                            },
                                        }
                                    );
                                });
                        },
                    }}
                    localization={LocalizationPTBr}
                    options={{
                        draggable: false,
                        sorting: false,
                        search: false,
                        paging: false,
                        rowStyle: (data) => {
                            const rowIncident = data as Incident;
                            if (rowIncident.id === incident.id) {
                                return {
                                    backgroundColor: red["100"],
                                };
                            }
                            return {};
                        },
                    }}
                />
            </Grid>
        </Grid>
    );
};

export { IncidentList };
